import React, { useState, useEffect } from "react";
import qs from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";

import { Language, isValidVerificationLink } from "../../utils";
import { Page, Confirm } from "../../components";

import makeClasses from "./styles";
import appLogo from "../../static/images/logo.svg";
import HelpIcon from "../../static/images/icons/help.svg";
import clsx from "clsx";
import { replace } from "connected-react-router";

import Disclaimer from "./steps/Disclaimer";
import StepOne from "./steps/StepOne";
import StepTwo from "./steps/StepTwo";
import StepThree from "./steps/StepThree";
import StepFour from "./steps/StepFour";
import StepFive from "./steps/StepFive";
import StepSix from "./steps/StepSix";
import IdentityStep from "./steps/StepSeven";
import SuccessStep from "./steps/StepEight";
import ExpiredStep from "./steps/StepNine";

const RegisterScreen = ({ history }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();
  const searchParams = qs.parse(history.location.search);

  const { languageTexts } = useSelector((state) => ({
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  const [registerStep, setRegisterStep] = useState(
    searchParams.step ? parseInt(searchParams.step) : 1,
  );
  const [affiliationStep1TraceId, setAffiliationStep1TraceId] = useState(
    searchParams.affiliationStep1TraceId || null,
  );
  const [affiliationStep2TraceId, setAffiliationStep2TraceId] = useState(
    searchParams.affiliationStep2TraceId || null,
  );
  const [affiliationStep3TraceId, setAffiliationStep3TraceId] = useState(
    searchParams.affiliationStep3TraceId || null,
  );
  const [phoneCountryId, setPhoneCountryId] = useState(null);
  const [ci, setCi] = useState(
    () => sessionStorage.getItem("register_ci") || "",
  );
  const [name, setName] = useState(
    () => sessionStorage.getItem("register_name") || "",
  );
  const [categoryId, setCategoryId] = useState(() => {
    const stored = sessionStorage.getItem("register_categoryId");
    return stored ? parseInt(stored) : null;
  });
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [hasVerificationLink, setHasVerificationLink] = useState(() => {
    const link = searchParams.verificationLink;
    return Boolean(link && isValidVerificationLink(link));
  });

  useEffect(() => {
    if (searchParams.result) {
      if (
        searchParams.result === "success" ||
        searchParams.result === "error"
      ) {
        setRegisterStep(9);
      } else if (searchParams.result === "expired") {
        setRegisterStep(10);
      }
    }
  }, [searchParams.result]);

  useEffect(() => {
    const params = qs.parse(history.location.search) || {};
    params.step = registerStep;
    if (affiliationStep1TraceId)
      params.affiliationStep1TraceId = affiliationStep1TraceId;
    dispatch(replace({ search: qs.stringify(params) }));
  }, [registerStep, affiliationStep1TraceId]);

  useEffect(() => {
    if (ci) sessionStorage.setItem("register_ci", ci);
  }, [ci]);

  useEffect(() => {
    if (name) sessionStorage.setItem("register_name", name);
  }, [name]);

  useEffect(() => {
    if (categoryId) sessionStorage.setItem("register_categoryId", categoryId);
  }, [categoryId]);

  const nextStep = (data = {}) => {
    if (data.ci) setCi(data.ci);
    if (data.name) setName(data.name);
    if (data.categoryId) setCategoryId(data.categoryId);
    if (data.traceId) setAffiliationStep1TraceId(data.traceId);
    if (data.affiliationStep2TraceId)
      setAffiliationStep2TraceId(data.affiliationStep2TraceId);
    if (data.affiliationStep3TraceId)
      setAffiliationStep3TraceId(data.affiliationStep3TraceId);
    if (data.email) setEmail(data.email);
    if (data.phone) setPhone(data.phone);
    if (data.phoneCountryId) setPhoneCountryId(data.phoneCountryId);
    setRegisterStep((prev) => prev + 1);
  };

  const resetToStart = () => {
    setRegisterStep(1);
    setAffiliationStep1TraceId(null);
    setAffiliationStep2TraceId(null);
    setAffiliationStep3TraceId(null);
    setPhoneCountryId(null);
    setCi("");
    setName("");
    setCategoryId(null);
    setEmail("");
    setPhone("");
    setHasVerificationLink(false);
    sessionStorage.removeItem("register_ci");
    sessionStorage.removeItem("register_name");
    sessionStorage.removeItem("register_categoryId");
    dispatch(replace({ search: qs.stringify({}) }));
  };

  const _toggleShowHelp = () => setShowHelp((prev) => !prev);

  const renderStep = () => {
    switch (registerStep) {
      case 1:
        return <Disclaimer nextStep={nextStep} />;
      case 2:
        return <StepOne nextStep={nextStep} />;
      case 3:
        return (
          <StepTwo
            ci={ci}
            name={name}
            categoryId={categoryId}
            nextStep={nextStep}
            history={history}
          />
        );
      case 4:
        return (
          <StepThree traceId={affiliationStep1TraceId} nextStep={nextStep} />
        );
      case 5:
        return (
          <StepFour
            traceId={affiliationStep1TraceId}
            affiliationStep2TraceId={affiliationStep2TraceId}
            email={email}
            nextStep={nextStep}
            history={history}
          />
        );
      case 6:
        return (
          <StepFive traceId={affiliationStep1TraceId} nextStep={nextStep} />
        );
      case 7:
        return (
          <StepSix
            traceId={affiliationStep1TraceId}
            affiliationStep3TraceId={affiliationStep3TraceId}
            phone={phone}
            phoneCountryId={phoneCountryId}
            nextStep={nextStep}
            history={history}
          />
        );
      case 8:
        return (
          <IdentityStep
            traceId={affiliationStep1TraceId}
            history={history}
            onVerificationLinkLoaded={() => setHasVerificationLink(true)}
          />
        );
      case 9:
        return <SuccessStep />;
      case 10:
        return <ExpiredStep onBackToStart={resetToStart} />;
      default:
        return null;
    }
  };

  return (
    <Page
      withHeader={false}
      withSideMenu={false}
      withBanner={false}
      withBoxBanner={false}
      showBranchWithNoPaymentMethodWarning={false}
      renderCustomToast={
        <ToastContainer
          className={classes.toast}
          position="top-center"
          autoClose={5000}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          hideProgressBar
          pauseOnFocusLoss={false}
          draggable={false}
          theme="light"
        />
      }>
      <div className={classes.container}>
        <div
          className={clsx(
            classes.contentContainer,
            registerStep === 8 && hasVerificationLink
              ? classes.contentContainerWithIFrame
              : null,
          )}>
          <div
            className={clsx(
              classes.contentWrapper,
              hasVerificationLink ? classes.contentWrapperWithIFrame : null,
            )}>
            <div className={classes.brandContainer}>
              <img
                src={appLogo}
                className={classes.brandLogo}
                alt="Handy Logo"
              />
              <div className={classes.brandSeparator} />
              <div className={classes.brandTitle}>
                {i18n.get("Register.PageTitle")}
              </div>
            </div>
          </div>
          {renderStep()}
        </div>
        <div className={classes.helpContainer} onClick={_toggleShowHelp}>
          {i18n.get("Register.UserHelpTitle")}
          <img src={HelpIcon} className={classes.helpIcon} alt="ayuda" />
        </div>
      </div>
      <Confirm
        open={showHelp}
        onClose={_toggleShowHelp}
        title={i18n.get("Register.UserHelpDialogTitle")}
        text={i18n.get("Register.UserHelpDialogText")}
        confirmText={i18n.get("Register.UserHelpDialogButton")}
        onConfirm={_toggleShowHelp}
      />
    </Page>
  );
};

RegisterScreen.id = "com.Handy.Register";

export default RegisterScreen;
